import type { InputField } from './types';

/**
 * Character limits for what the user types into an approval card. Mirrors
 * shared/hitl_input_limits.py on the API, which re-checks on submit: the card
 * warns and blocks first, the server is the backstop.
 */
export const TEXT_MAX_CHARS = 500;
export const TEXTAREA_MAX_CHARS = 2000;
export const FEEDBACK_MAX_CHARS = 2000;

/** The counter appears once the text reaches this share of its limit. */
const COUNTER_FROM = 0.8;

/** The limit for one field: the author's override, else the typed default, else none. */
export function maxCharsFor(field: Pick<InputField, 'type' | 'max_length'>): number | null {
  if (typeof field.max_length === 'number' && field.max_length > 0) return Math.floor(field.max_length);
  switch (field.type) {
    case 'text':
    case 'email':
      return TEXT_MAX_CHARS;
    case 'textarea':
      return TEXTAREA_MAX_CHARS;
    default:
      return null;
  }
}

export interface CharCount {
  /** Characters past the limit; 0 when within it. */
  readonly over: number;
  /** What to show under the control: a counter near the limit, an error past it, or nothing. */
  readonly label: string | null;
}

export function charCount(length: number, limit: number | null): CharCount {
  if (limit === null) return { over: 0, label: null };
  const over = Math.max(0, length - limit);
  if (over > 0) {
    return { over, label: `Too long by ${over} character${over === 1 ? '' : 's'} (${length} / ${limit})` };
  }
  if (length >= Math.ceil(limit * COUNTER_FROM)) return { over: 0, label: `${length} / ${limit}` };
  return { over: 0, label: null };
}
